import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, IconButton, ToggleButtonGroup, ToggleButton,
  CircularProgress, Chip, Table, TableHead, TableBody, TableRow, TableCell,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import type { BucketBy } from '../../types';
import { InteractiveLineChart } from './chartsLazy'; // Perf 3g: recharts loads on first chart render
import { widgetDataApi } from '../../services/api';

interface SparklineDetailDialogProps {
  open: boolean;
  onClose: () => void;
  metricId: number;
  metricName: string;
  unit?: string | null;
  quarterStart?: string;
  quarterEnd?: string;
  /** F4 — run-rate landing estimate from the scorecard's pace math. */
  projection?: { value: number; target: number | null } | null;
  /** What the metric tracks / how it pulls data (composed by metricExplainer). */
  about?: { what: string | null; how: string; target: string | null; ownerName?: string | null } | null;
}


const SparklineDetailDialog: React.FC<SparklineDetailDialogProps> = ({
  open, onClose, metricId, metricName, unit, quarterStart, quarterEnd, projection, about,
}) => {
  const [bucketBy, setBucketBy] = useState<BucketBy>('weeks');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{
    currentValue: number;
    previousValue: number | null;
    changePercent: number | null;
    changeAbsolute: number | null;
    trend: 'up' | 'down' | 'flat';
    periodLabel: string;
    previousPeriodLabel: string | null;
    timeSeries: Array<{ date: string; value: number; label: string }>;
    breakdown: Array<{ label: string; value: number }>;
    metricUnit: string | null;
    metricName: string | null;
    isRateMetric: boolean;
  } | null>(null);

  const loadData = useCallback(async () => {
    if (!metricId || !open) return;
    setLoading(true);
    try {
      const result = await widgetDataApi.aggregate({
        metricId,
        dateStart: quarterStart,
        dateEnd: quarterEnd,
        bucketBy,
        comparisonPeriod: 'previous_period',
      });
      if (result.success && result.data) {
        setData(result.data);
      }
    } catch (err) {
      console.error('Failed to load sparkline detail:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [metricId, open, bucketBy, quarterStart, quarterEnd]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Semantic up/down/flat routes through the AUD-112 status tokens
  // (scorecard rebuild P3 — was hardcoded #4caf50/#CF6679).
  const trendColor = data?.trend === 'up' ? 'var(--status-on-track)' : data?.trend === 'down' ? 'var(--status-off-track)' : 'var(--status-neutral)';
  const TrendIcon = data?.trend === 'up' ? TrendingUpIcon : data?.trend === 'down' ? TrendingDownIcon : TrendingFlatIcon;
  const isRate = data?.isRateMetric || false;
  const displayUnit = unit || (isRate ? '%' : '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
      sx: { borderRadius: '6px', maxWidth: 960, mx: 'auto', border: '1px solid var(--rule)', backgroundImage: 'none' }
    }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography sx={{ fontFamily: '"Inter Tight", "Inter", sans-serif', fontWeight: 700, fontSize: 20, letterSpacing: '-0.02em' }}>{metricName}</Typography>
          {data && <Typography variant="caption" color="text.secondary">{data.periodLabel}</Typography>}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* KPI Summary Cards */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 180px', p: 2, bgcolor: 'action.hover', borderRadius: '6px' }}>
                <Typography variant="caption" color="text.secondary">Current Period</Typography>
                <Typography variant="h4" fontWeight={700}>
                  {data.currentValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}{displayUnit ? ` ${displayUnit}` : ''}
                </Typography>
              </Box>
              {data.previousValue != null && (
                <Box sx={{ flex: '1 1 180px', p: 2, bgcolor: 'action.hover', borderRadius: '6px' }}>
                  <Typography variant="caption" color="text.secondary">Previous Period</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ opacity: 0.6 }}>
                    {data.previousValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}{displayUnit ? ` ${displayUnit}` : ''}
                  </Typography>
                </Box>
              )}
              {projection && (
                <Box sx={{ flex: '1 1 180px', p: 2, bgcolor: 'action.hover', borderRadius: '6px' }}>
                  <Typography variant="caption" color="text.secondary">Projected quarter end</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: projection.target !== null ? (projection.value >= projection.target ? 'var(--status-on-track)' : 'var(--status-warning)') : 'inherit' }}>
                    {projection.value.toLocaleString(undefined, { maximumFractionDigits: 1 })}{displayUnit ? ` ${displayUnit}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    at the current run rate{projection.target !== null ? ` · target ${projection.target.toLocaleString()}` : ''}
                  </Typography>
                </Box>
              )}
              <Box sx={{ flex: '1 1 180px', p: 2, bgcolor: 'action.hover', borderRadius: '6px' }}>
                <Typography variant="caption" color="text.secondary">Change</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendIcon sx={{ color: trendColor, fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={700} sx={{ color: trendColor }}>
                    {data.changePercent != null ? `${data.changePercent > 0 ? '+' : ''}${data.changePercent.toFixed(1)}%` : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* About this metric — what it tracks + how it pulls its data */}
            {about && (
              <Box sx={{ p: 2, border: '1px solid var(--rule)', borderRadius: '4px', bgcolor: 'var(--paper-2)' }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-faint)', mb: 0.5 }}>
                  About this metric
                </Typography>
                {about.what && (
                  <Typography sx={{ fontSize: 13.5, color: 'var(--ink)', mb: 0.5 }}>{about.what}</Typography>
                )}
                <Typography sx={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>{about.how}</Typography>
                {(about.target || about.ownerName) && (
                  <Typography sx={{ fontSize: 12.5, color: 'var(--ink-faint)', mt: 0.5 }}>
                    {[about.target ? `Target: ${about.target}` : null, about.ownerName ? `Owner: ${about.ownerName}` : null]
                      .filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            )}

            {/* Bucket toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight={600}>Performance Trend</Typography>
              <ToggleButtonGroup value={bucketBy} exclusive onChange={(_, v) => v && setBucketBy(v)} size="small">
                <ToggleButton value="days">Days</ToggleButton>
                <ToggleButton value="weeks">Weeks</ToggleButton>
                <ToggleButton value="months">Months</ToggleButton>
                <ToggleButton value="years">Years</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Interactive chart */}
            <InteractiveLineChart
              data={data.timeSeries}
              height={340}
              showArea
              showPoints
              unit={displayUnit}
              isRateMetric={isRate}
            />

            <Divider />

            {/* Data table */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Weekly Breakdown</Typography>
              <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Value</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>vs Previous</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.timeSeries.map((point, idx) => {
                      const prev = idx > 0 ? data.timeSeries[idx - 1].value : null;
                      const change = prev != null ? point.value - prev : null;
                      const pct = prev != null && prev !== 0 ? ((point.value - prev) / Math.abs(prev)) * 100 : null;
                      return (
                        <TableRow key={point.date} hover>
                          <TableCell>{point.label}</TableCell>
                          <TableCell align="right">
                            {point.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}{displayUnit ? ` ${displayUnit}` : ''}
                          </TableCell>
                          <TableCell align="right">
                            {change != null ? (
                              <Chip
                                size="small"
                                label={pct != null ? `${change > 0 ? '+' : ''}${pct.toFixed(1)}%` : '—'}
                                sx={{
                                  bgcolor: change > 0 ? 'var(--status-on-track-soft)' : change < 0 ? 'var(--status-off-track-soft)' : 'action.hover',
                                  color: change > 0 ? 'var(--status-on-track-on-soft)' : change < 0 ? 'var(--status-off-track-on-soft)' : 'text.secondary',
                                  fontWeight: 600, fontSize: '12.5px',
                                }}
                              />
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography color="text.secondary">Failed to load data</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SparklineDetailDialog;
